import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isEmpty, pickBy } from 'lodash';
import {
  FUND_INVESTMENT_BY_ALLOCATION_QUERY_KEY,
  FUND_INVESTMENT_SERVICES_ERROR_MESSAGES,
  UPDATE_FUND_INVESTMENT_DETAILS_DEFAULT_ERROR_MESSAGE,
  UPDATE_FUND_INVESTMENT_DETAILS_DEFAULT_SUCCESS_MESSAGE,
} from 'common/constants/services/fundInvestments';
import { SuperAgentError } from 'common/types/services';
import { FundInvestmentsService } from 'services';
import { getNumberValue, getObjectValue, throwError } from 'utilities';
import {
  UpdateFundInvestmentDetailsParams,
  UpdateFundInvestmentDetailsResponse,
  UseUpdateFundInvestmentDetailsReturn,
} from './useUpdateFundInvestmentDetails.types';

const updateFundInvestment = async (
  params: UpdateFundInvestmentDetailsParams
): Promise<UpdateFundInvestmentDetailsResponse | null> => {
  const { fundInvestmentDetails } = params;

  if (isEmpty(pickBy(fundInvestmentDetails))) {
    return throwError(UPDATE_FUND_INVESTMENT_DETAILS_DEFAULT_ERROR_MESSAGE);
  }
  const fundInvestmentsService = new FundInvestmentsService();

  try {
    const { allocation: allocationId } = getObjectValue(fundInvestmentDetails);

    const updatedFundInvestment = await fundInvestmentsService.updateFundInvestmentDetails(
      getNumberValue(allocationId),
      fundInvestmentDetails
    );

    if (!isEmpty(updatedFundInvestment)) {
      return {
        fundInvestment: updatedFundInvestment,
        responseMessage: UPDATE_FUND_INVESTMENT_DETAILS_DEFAULT_SUCCESS_MESSAGE,
      } as UpdateFundInvestmentDetailsResponse;
    }

    return throwError(UPDATE_FUND_INVESTMENT_DETAILS_DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    const { status } = error as SuperAgentError;

    return throwError(
      FUND_INVESTMENT_SERVICES_ERROR_MESSAGES?.[getNumberValue(status)] ??
        UPDATE_FUND_INVESTMENT_DETAILS_DEFAULT_ERROR_MESSAGE,
      error
    );
  }
};

const useUpdateFundInvestment = (): UseUpdateFundInvestmentDetailsReturn => {
  const queryClient = useQueryClient();

  const updateFundInvestmentMutation: UseMutationResult<
    UpdateFundInvestmentDetailsResponse | null, // Response type (null if error)
    Error, // Error type
    UpdateFundInvestmentDetailsParams // Parameters type
  > = useMutation({
    mutationFn: updateFundInvestment,
  });

  const { isError, isPending: isLoading, isSuccess, mutateAsync } = updateFundInvestmentMutation;

  if (isSuccess) {
    // Invalidate queries to refetch the data after mutation success
    queryClient.invalidateQueries({
      queryKey: [
        FUND_INVESTMENT_BY_ALLOCATION_QUERY_KEY, // Fund investment by allocation
      ],
    });

    // Clean the mutation internal state
    updateFundInvestmentMutation.reset();
  }

  return {
    isError,
    isLoading,
    mutateAsync,
  };
};

export default useUpdateFundInvestment;
